import { FunctionComponent, memo, useMemo, useState } from "react";
import { IProps } from "./users-subscribed-table.types";
import { IUser, Route } from "@edgetier/watchtower-types";
import { useInfiniteQuery } from "react-query";
import { requestUserSubscribed } from "./users-subscribed-table.utilities";
import { BooleanIcon, Cell, Column, Heading, Table, TableColumns, TableFooter } from "@edgetier/client-components";
import { Button, SpinnerUntil } from "@edgetier/components";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import RemoveUsersSubscribed from "components-for/users/users-subscribed/remove-users-subscribed";
import UsersSubscribeButton from "components-for/users/users-subscribed/users-subscribe-button";
import { USERS_SUBSCRIBED_ORDER_BY } from "./users-subscribed-table.constants";
import "./users-subscribed-table.scss";

/**
 * Component showing a table with all user subscribed to a tag
 * @param props.modelId
 * @param props.search      Any search string from filters
 */
const UsersSubscribedTable: FunctionComponent<IProps> = ({ modelId: initialModelId, search, tagId }) => {
    const [modelId, setModelId] = useState(initialModelId);
    const parameters = {
        search,
        orderBy: USERS_SUBSCRIBED_ORDER_BY,
    };
    const requestUrl = Route.ModelSubscriptions.replace(":modelId", modelId!);

    // Request anomalies in the date range. There is a "load more" button, so this is an infinite query.
    const { hasNextPage, data, fetchNextPage, isFetchingNextPage, isFetching } = useInfiniteQuery(
        [requestUrl, parameters],
        ({ pageParam }) => requestUserSubscribed(requestUrl, parameters, pageParam),
        { enabled: typeof modelId === "string", getNextPageParam: ({ pagination }) => pagination.nextPage }
    );

    // Combine pages of users into one array.
    const usersSubscribed = useMemo(() => (data?.pages ?? []).flatMap((users) => users.items), [data]);

    return (
        <div className="users-subscribed-table">
            <SpinnerUntil<IUser[]> data={usersSubscribed} isReady={!isFetching}>
                {(data) => (
                    <Table<IUser> getRowKey={({ userId }) => userId} headerIsSticky rows={isFetching ? null : data}>
                        <TableColumns>
                            <Column>
                                <Heading>Name</Heading>
                                <Cell<IUser>>{({ firstName, surname }) => `${firstName} ${surname}`}</Cell>
                            </Column>

                            <Column>
                                <Heading>Email Address</Heading>
                                <Cell<IUser>>{({ emailAddress }) => emailAddress}</Cell>
                            </Column>
                            <Column>
                                <Heading className="users-subscribed-table__email-notification">
                                    Email Notification
                                </Heading>
                                <Cell<any>>{() => <BooleanIcon value={true} />}</Cell>
                            </Column>
                            {typeof modelId === "string" && (
                                <Column>
                                    <Heading>&nbsp;</Heading>
                                    <Cell<IUser>>
                                        {({ userId }) => <RemoveUsersSubscribed userId={userId} modelId={modelId} />}
                                    </Cell>
                                </Column>
                            )}
                        </TableColumns>
                        <TableFooter>
                            {hasNextPage && (
                                <Button
                                    className="users-subscribed-table__load-more"
                                    icon={faEllipsisV}
                                    isLoading={isFetchingNextPage !== false}
                                    onClick={() => fetchNextPage()}
                                >
                                    Load More Users
                                </Button>
                            )}
                            <UsersSubscribeButton onCreateModel={setModelId} tagId={tagId} modelId={modelId} />
                        </TableFooter>
                    </Table>
                )}
            </SpinnerUntil>
        </div>
    );
};

export default memo(UsersSubscribedTable);
